//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Abortable Multi-Fetch Composable
 *
 * Provides utilities for managing multiple concurrent AbortController instances
 * with automatic cleanup on component unmount.
 *
 * Responsibilities:
 * - Track multiple active AbortControllers in a Set
 * - Wrap async operations with abort signal support
 * - Return null on abort instead of throwing (graceful handling)
 * - Abort all pending requests on component unmount
 * - Support bulk abort for cleanup scenarios (logout, navigation, etc.)
 *
 * Use Cases:
 * - API composables that fetch from multiple endpoints
 * - Preventing memory leaks from abandoned requests
 * - Graceful request cancellation on component unmount
 * - Avoiding race conditions when fetching with outdated parameters
 */
export function useAbortableMultiFetch() {
  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const controllers: Set<AbortController> = new Set();

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Wrap an async operation with AbortController
   *
   * Purpose:
   * Execute async function with abort signal and automatic controller cleanup.
   * Returns null on abort to enable graceful handling without throwing.
   *
   * Responsibilities:
   * - Create and register new AbortController
   * - Pass abort signal to provided function
   * - Return null if request is aborted (no error throw)
   * - Re-throw non-abort errors
   * - Cleanup controller from tracking Set on completion
   *
   * @param fn - Async function that accepts AbortSignal
   * @returns Result of function or null if aborted
   */
  const withAbort = async <T>(fn: (signal: AbortSignal) => Promise<T>): Promise<T | null> => {
    const controller = new AbortController();
    controllers.add(controller);

    try {
      // Attach a no-op catch synchronously before awaiting.
      // V8 marks a rejected Promise as "Uncaught" at the end of the microtask
      // turn in which it was created — BEFORE the rejection has had a chance to
      // propagate up the async chain to this try/catch block. Registering a
      // handler immediately tells V8 the rejection IS handled, suppressing the
      // false "Uncaught (in promise)" console warning while still letting the
      // real catch block below process the error.
      const promise = fn(controller.signal);
      promise.catch(() => {});
      return await promise;
    } catch (err: any) {
      if (err.name === "AbortError" || err.cause?.name === "AbortError" || controller.signal.aborted) {
        return null;
      }
      throw err;
    } finally {
      controllers.delete(controller);
    }
  };

  /**
   * Abort all tracked requests
   *
   * Purpose:
   * Cancel all pending requests and clear controller tracking.
   * Used for cleanup on logout, navigation, or component unmount.
   *
   * Responsibilities:
   * - Abort all registered AbortControllers
   * - Clear the tracking Set
   * - Trigger \"AbortError\" in all pending withAbort calls
   */
  const abortAll = () => {
    controllers.forEach((c) => c.abort());
    controllers.clear();
  };

  // Only attach lifecycle hook if inside a component setup
  if (getCurrentInstance()) {
    onUnmounted(abortAll);
  }

  return { withAbort, abortAll };
}
